import { RefObject, useCallback, useMemo, useRef } from 'react';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ui } from '@procuros/datachecks';

import { SchemableFormNavigationContextProvider } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContextProvider';
import { getCanonical } from 'services/datachecks/datachecks';
import { logError } from 'services/logging/logging';
import { TaskErrorsServiceSingleton } from 'services/taskErrors';
import { useTaskErrors } from 'services/taskErrors/hooks';
import { TaskError } from 'services/taskErrors/types';
import { getTaskErrorFieldPaths } from 'services/taskErrors/utils';
import { useInitTaskStore } from 'services/tasks/taskStore';
import { DisplayModes } from 'services/webedi/types';
import { useTaskStore } from 'stores/tasks/tasks';
import { datachecksResolver } from 'support/helpers/resolvers/datachecksResolver';
import { ProcessSpecificationDTO, TaskBaseDTO, TaskDTO } from 'support/types';
import { Form } from 'components/Form/Form';
import {
  FileUploadFn,
  GetFileDownloadUrlFn,
} from 'components/SchemableForm/contexts/SchemableFormFileUploadContext/SchemableFormFileUploadContext';
import { SchemableFormVariablesContextProvider } from 'components/SchemableForm/contexts/SchemableFormVariablesContext/SchemableFormVariablesContextProvider';
import { SchemableForm } from 'components/SchemableForm/SchemableForm';
import { FileUploadWrapper } from 'components/TaskForm/components/FileUploadWrapper';
import { TaskReactiveUiConfig } from 'components/TaskForm/components/TaskReactiveUiConfig';
import { useInitialTaskSetup } from 'components/TaskForm/hooks/useInitialTaskSetup';

export const TASK_FORM_ID = 'task-form';

type TaskFormProps = {
  task: TaskBaseDTO | TaskDTO;
  mode: DisplayModes;
  isSubmitting: boolean;
  variables: Record<string, string>;
  relationshipId?: string;
  onSubmit?: (canonicalData: Record<string, unknown>) => void;
  onValidationError?: (errors: FieldErrors, data: Record<string, unknown>) => void;
  uploadFile?: FileUploadFn;
  getFileDownloadUrl?: GetFileDownloadUrlFn;
};

const ROOT_FIELD: NonNullable<TaskBaseDTO['actions']>[number] = {
  path: '$',
  name: '',
  description: '',
  type: 'object',
  modality: {
    type: 'MANDATORY',
    default: 'MANDATORY',
  },
  options: {
    display: {
      format: {
        value: 'object',
      },
    },
  },
};

const EMPTY_DATA = {};
export const TaskForm = ({
  task,
  mode,
  isSubmitting,
  variables,
  relationshipId,
  onSubmit,
  onValidationError,
  uploadFile,
  getFileDownloadUrl,
}: TaskFormProps) => {
  useInitTaskStore();
  const { t } = useTranslation();

  const response = 'response' in task ? task.response || EMPTY_DATA : EMPTY_DATA;

  const actions = useMemo(() => {
    if (!task.actions) {
      return undefined;
    }
    return [ROOT_FIELD, ...task.actions];
  }, [task.actions]);

  const resolver = useMemo(
    () =>
      datachecksResolver({
        fieldsConfiguration: actions as ProcessSpecificationDTO['fields'] | undefined,
        withTimeout: true,
        modalityErrorMessages: {
          title: t('connections:relationshipDetails.task.errorMessages.required.title'),
          description: t('connections:relationshipDetails.task.errorMessages.required.description'),
        },
      }),
    [actions, t],
  );

  const formRef = useRef<HTMLFormElement>(null);

  const formMethods = useForm({ resolver, reValidateMode: 'onChange', shouldFocusError: false });

  const onFormSubmit = useCallback(async () => {
    if (!onSubmit || !actions) {
      return;
    }

    try {
      const fields = actions as ProcessSpecificationDTO['fields'];
      const { canonical } = await getCanonical(fields, formMethods.getValues());
      onSubmit(ui.stripHelperFields(canonical));
    } catch (error) {
      logError(error);
    }
  }, [onSubmit, actions, formMethods]);

  const onFormInvalid = useCallback(
    (errors: FieldErrors) => {
      onValidationError?.(errors, formMethods.getValues());

      TaskErrorsServiceSingleton.scrollToFirstField(getTaskErrorFieldPaths(errors), { block: 'nearest' });
    },
    [onValidationError, formMethods],
  );

  return (
    <FormProvider {...formMethods}>
      <Form id={TASK_FORM_ID} ref={formRef} onSubmit={formMethods.handleSubmit(onFormSubmit, onFormInvalid)}>
        <TaskFormInner
          initialData={response}
          actions={actions}
          mode={mode}
          isSubmitting={isSubmitting}
          variables={variables}
          relationshipId={relationshipId}
          task={task}
          uploadFile={uploadFile}
          getFileDownloadUrl={getFileDownloadUrl}
          containerRef={formRef}
        />
      </Form>
    </FormProvider>
  );
};

type TaskFormInnerProps = {
  task: TaskBaseDTO;
  containerRef: RefObject<HTMLFormElement | null>;
  initialData: Record<string, unknown> | undefined;
  actions: NonNullable<TaskBaseDTO['actions']> | undefined;
  mode: DisplayModes;
  isSubmitting: boolean;
  variables: Record<string, string>;
  relationshipId?: string;
  uploadFile?: FileUploadFn;
  getFileDownloadUrl?: GetFileDownloadUrlFn;
};

const TaskFormInner = ({
  task,
  containerRef,
  initialData,
  actions,
  mode,
  isSubmitting,
  variables,
  relationshipId,
  uploadFile,
  getFileDownloadUrl,
}: TaskFormInnerProps) => {
  useInitialTaskSetup({ actions, initialData });
  const uiConfig = useTaskStore((state) => state.uiConfig);
  const isLoading = useTaskStore((state) => state.isLoading);

  useTaskErrors(variables);

  if (isLoading || uiConfig.length === 0) {
    return null;
  }

  return (
    <section className="flex flex-col gap-6">
      <TaskReactiveUiConfig actions={actions} />
      <SchemableFormNavigationContextProvider<TaskError>
        service={TaskErrorsServiceSingleton}
        NavigableItemPopoverComponent={null}
        containerRef={containerRef}
      >
        <FileUploadWrapper
          relationshipId={relationshipId}
          taskId={task.id}
          uploadFile={uploadFile}
          getFileDownloadUrl={getFileDownloadUrl}
        >
          <SchemableFormVariablesContextProvider variables={variables}>
            {uiConfig.map((section) => {
              if (section.type !== 'info') {
                return null;
              }
              return (
                <SchemableForm
                  key={section.type}
                  dataPathPrefix={`${section.type}.0`}
                  config={section as ui.InfoSection}
                  mode={mode}
                  isSubmitting={isSubmitting}
                  variant="generic"
                />
              );
            })}
          </SchemableFormVariablesContextProvider>
        </FileUploadWrapper>
      </SchemableFormNavigationContextProvider>
    </section>
  );
};
